import { useDispatch, useSelector } from "react-redux";
import { getModule } from "store/userSlice";
import { useOutletContext } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import EvaluationStats from "components/Evaluation/EvaluationStats";
import * as Sentry from "@sentry/react";
import { api } from "helpers/axios";
import { errorMessage } from "helpers/common";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import { fetchGroups, getGroups } from "store/groupsSlice";
import { Select } from "components/ui";
import { isDbModule } from "helpers/module";

export default function Cockpit() {
  const dispatch = useDispatch();

  const { translate, editableTranslate } = useTranslation();

  const { overlay } = useOutletContext();

  const groups = useSelector(getGroups);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [evaluationGroupStats, setEvaluationGroupStats] = useState(null);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedTemplateType, setSelectedTemplateType] = useState(null);

  const currentModule = useSelector(getModule);

  const isDbModuleActive = useMemo(() => {
    return isDbModule(currentModule);
  }, [currentModule]);

  const groupOptions = useMemo(() => {
    if (!groups) return [];

    const mapped = groups.map(g => ({ value: g.hash_id, label: g.name }));

    return mapped;
  }, [groups]);

  const templateTypeOptions = useMemo(() => {
    return [
      { value: 'regular', label: translate('state_employee.rating_mode.template_regular') },
      { value: 'leadership', label: translate('state_employee.rating_mode.template_leadership') }
    ];
  }, [translate]);

  useEffect(() => {
    dispatch(fetchGroups());
    fetchEvaluationGroupStats();
  }, []);

  useEffect(() => {
    fetchEvaluationGroupStats();
  }, [selectedGroup, selectedTemplateType]);

  const fetchEvaluationGroupStats = async () => {
    setError(null);
    setEvaluationGroupStats(null);
    setLoading(true);
    try {
      const filters = [];
      if (selectedGroup && selectedGroup?.value !== '-') filters.push('group=' + selectedGroup.value);
      if (selectedTemplateType) filters.push('template_type=' + selectedTemplateType.value);

      let url = `/employee-balanced-scorecards/state-evaluation-group-stats`;
      if (filters.length > 0) url += '?' + filters.join('&');

      const { data, status } = await api.get(url);
      if (status === 200) {
        setEvaluationGroupStats(data.data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch evaluation group stats'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <h4>{editableTranslate('global.menu.evaluation_statistics.cockpit', 'Cockpit')}</h4>
          <p className="section-description">{editableTranslate('evaluation_statistics.cockpit_description')}</p>

          {
            error && <div>
              <ErrorAlert error={error} onClose={() => setError(null)} />
            </div>
          }

          {
            !isDbModuleActive ? <>
              {overlay}
            </> : <div className="mt-4">
              <div className="mb-4 gray-box p-3">
                <div>
                  <div style={{ display: 'contents' }} data-testid="period-filter-select">
                    <Select className="d-inline-block min-w-200" placeholder={translate('evaluation_statistics.filters.select_group', 'Gruppe auswählen')} options={groupOptions} value={selectedGroup} onChange={v => setSelectedGroup(v)} isClearable={true} />
                  </div>

                  <div style={{ display: 'contents' }} data-testid="group-filter-select">
                    <Select className="d-inline-block min-w-200 ml-3" placeholder={translate('evaluation_statistics.filters.select_template', 'Vorlage auswählen')} options={templateTypeOptions} value={selectedTemplateType} onChange={v => setSelectedTemplateType(v)} isClearable={true} />
                  </div>
                </div>
              </div>
              {
                (loading && !evaluationGroupStats) && <div className="spinner-border spinner-border-sm" role="status">
                  <span className="sr-only">Wird geladen...</span>
                </div>
              }
              {
                (!loading && (!evaluationGroupStats || evaluationGroupStats.length === 0)) && <div data-testid="empty-state">{editableTranslate('global.no_data')}</div>
              }
              {
                evaluationGroupStats && evaluationGroupStats.map((stat, i) => <div key={i} className="mb-3">
                  <div className="font-weight-bold mb-2">{stat.group}</div>
                  <EvaluationStats id={stat.id} data={stat} centered={false} thirdCol={false} />
                </div>)
              }
            </div>
          }
        </div>
      </div>
    </>
  );
}
